import vueStore from "@/store";
import LocalStoreManager from "@/utilities/local-store-manager";
import receiptsProxy from "@/proxies/back-end/receipts/receipts";
import { validateUserPermission } from "@/utilities/app-access-util";
import { logEvent } from "@/utilities/analytics";

import { Constants } from "customer-sdk/constants";

const ACCOUNTING_QUESTIONNAIRE_CONSTANTS = Constants.ACCOUNTING_QUESTIONNAIRE_CONSTANTS;
const ACCOUNTING_QUESTIONNAIRE_PAGES = Constants.ACCOUNTING_QUESTIONNAIRE_PAGES;
const COMPANY_STATUSES = Constants.COMPANY_STATUSES;
const PAGE_URLS = Constants.PAGE_URLS;
const COMPANY_USER_ACCESS_ROLES = Constants.COMPANY_USER_ACCESS_ROLES;

const validateIsUserCompanyAdmin = (component) => {
  const store = new LocalStoreManager();
  const userId = store.getUserId();
  return validateUserPermission(userId, component.companyUsers, [COMPANY_USER_ACCESS_ROLES.OWNER]);
};

export const ACCOUNTING_QUESTIONNAIRE_ALLOWED_STATUSES_TO_ENABLE = [
  COMPANY_STATUSES.LIVE.value,
  COMPANY_STATUSES.LIVE_POST_INCORPORATION.value,
  COMPANY_STATUSES.CHURN_REQUESTED.value,
  COMPANY_STATUSES.CHURN_PROCESS.value,
  COMPANY_STATUSES.STRIKING_OFF.value,
];

export const DssAccessibilityMixin = {
	methods: {
		async isAccountingDssAccessibleForCurrentCompany() {
			const store = new LocalStoreManager();

      const authToken = store.getToken();
      const companyId = store.getCompanyId();

			let isDSSAccessibleForCompanyCached = vueStore.state.codingEngineModule.isDSSAccessibleForCompany;

			if(isDSSAccessibleForCompanyCached && (isDSSAccessibleForCompanyCached[companyId] == true || isDSSAccessibleForCompanyCached[companyId] == false)) {
				return isDSSAccessibleForCompanyCached[companyId];
			} else if(!isDSSAccessibleForCompanyCached) {
				isDSSAccessibleForCompanyCached = {};
			}

      const isDssAccessibilityCheckedCached = vueStore.state.codingEngineModule.isDssAccessibilityChecked;
      const response = await receiptsProxy.isAccountingDssAccessible(
        authToken,
        companyId,
        isDssAccessibilityCheckedCached != true
      );

      vueStore.commit("codingEngineModule/setIsDssAccessibilityChecked", true);
      
      const isDSSAccessibleForCompany = response?.is_dss_accessible;
      isDSSAccessibleForCompanyCached[companyId] = isDSSAccessibleForCompany;

      vueStore.commit("codingEngineModule/setIsDSSAccessibleForCompany", isDSSAccessibleForCompanyCached);

			return isDSSAccessibleForCompany;
    },

    async isAccountingTileAccessibleForCurrentCompany() {
      const store = new LocalStoreManager();
      const authToken = store.getToken();
      const companyId = store.getCompanyId();

			let isAccountingTileAccessibleForCompanyCached = vueStore.state.codingEngineModule.isAccountingTileAccessibleForCompany;

			if(isAccountingTileAccessibleForCompanyCached && (isAccountingTileAccessibleForCompanyCached[companyId] == true || isAccountingTileAccessibleForCompanyCached[companyId] == false)) {
				return isAccountingTileAccessibleForCompanyCached[companyId];
			} else if(!isAccountingTileAccessibleForCompanyCached) {
				isAccountingTileAccessibleForCompanyCached = {};
			}

      const response = await receiptsProxy.isAccountingTileAccessible(
        authToken,
        companyId,
      );
      
      const isAccountingTileAccessibleForCompany = response?.is_tile_accessible;
      isAccountingTileAccessibleForCompanyCached[companyId] = isAccountingTileAccessibleForCompany;

      vueStore.commit("codingEngineModule/setIsAccountingTileAccessibleForCompany", isAccountingTileAccessibleForCompanyCached);

			return isAccountingTileAccessibleForCompany;
    },

    getAccountingQuestionnaireStatusClassForCurrentCompany(component) {
      const { company, accountingQuestionnaireStatus } = component;
      const companyOwner = validateIsUserCompanyAdmin(component);
      let allowedStatuses = ACCOUNTING_QUESTIONNAIRE_ALLOWED_STATUSES_TO_ENABLE;

      if (company && accountingQuestionnaireStatus === ACCOUNTING_QUESTIONNAIRE_CONSTANTS.STATUSES.DONE) {
        return "done";
      }

      if (!companyOwner && !this.isNonAdminHasAccessToQuestionnaire(component)) return "incomplete";

      if (company.is_transfer) {
        allowedStatuses = component.ALLOWED_STATUSES;
      }

      // SA-32677
      if (company && company.uen && allowedStatuses.includes(company.status)) {
        return "active";
      }

      return "incomplete";
    },

    isNonAdminHasAccessToQuestionnaire(component) {
      const store = new LocalStoreManager();
      const userId = store.getUserId();
      if(component.companyUsers) {
        const hasFinanceOrPayrollDetails = component.companyUsers.some(member => (
          member.questionnaire_permitted_pages && 
          (member.questionnaire_permitted_pages.includes(ACCOUNTING_QUESTIONNAIRE_PAGES.FINANCE_DETAILS) ||
          member.questionnaire_permitted_pages.includes(ACCOUNTING_QUESTIONNAIRE_PAGES.PAYROLL_DETAILS)) &&
          member.user._id === userId 
      ));
        return hasFinanceOrPayrollDetails; 
      }
      return false;
    },

    goToAccountingSetupForCurrentCompany(component) {
      const getAccountingQuestionnaireStatusClassForCurrentCompany = this.getAccountingQuestionnaireStatusClassForCurrentCompany(component);
      const companyOwner = validateIsUserCompanyAdmin(component);

      if(
        (companyOwner || this.isNonAdminHasAccessToQuestionnaire(component)) &&
        (
          getAccountingQuestionnaireStatusClassForCurrentCompany == "active" ||
          getAccountingQuestionnaireStatusClassForCurrentCompany == "done"
        )
      ) {
        const store = new LocalStoreManager();
        
        const companyId = store.getCompanyId();
        const userId = store.getUserId();
        // go to accounting questionaires
        logEvent.handleWriteToMixpanel("Click on Accounting Set Up", {
          "Company ID": companyId,
          userId: userId,
        });
        window.location.href = PAGE_URLS.ACCOUNTING_SETUP;
      } else {
        if(companyOwner) {
          // show popup if company owner
          if(component.onUpdateDialog) {
            component.onUpdateDialog(false);
          }
          
          component.dialogAccountingSetup = true;
        }
      }
    },

    getCompanyActionIndicatorForCurrentCompany(component) {
      const { company } = this;
      const getAccountingQuestionnaireStatusClassForCurrentCompany = this.getAccountingQuestionnaireStatusClassForCurrentCompany(component);
      if(
        getAccountingQuestionnaireStatusClassForCurrentCompany == "incomplete"
      ) {
        return "/imgs/dashboard/indicator-details.svg";
      } else if(getAccountingQuestionnaireStatusClassForCurrentCompany == "done") {
        return "";
      } else {
        return company ? "/imgs/dashboard/indicator-start.svg" : "/imgs/dashboard/indicator-details.svg";
      }
    },

    isAccountingDssCompanyOwnerForCurrentCompany(component) {
      return validateIsUserCompanyAdmin(component);
    }
	}
}